import React, { useState, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

import {
  generateDateRange,
  getGeorgiaHolidays,
  handleDateClick,
  populatePattern,
  countOvernights,
  PatternConfig,
} from "../../utils/calendarUtils";

import ParentTabs from "./ParentTabs";
import PatternControls from "./PatternControls";
import MonthGrid from "./MonthGrid";
import OvernightCounter from "./OvernightCounter";

export interface ParentingTimeCalendarProps {
  parentName1: string;
  parentName2: string;
  nonCustodialParent: string;
  calendarData: Record<string, string>;
  onCalendarChange: (data: Record<string, string>) => void;
  parentFields?: React.ReactNode;
}

function todayISO(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

const ParentingTimeCalendar: React.FC<ParentingTimeCalendarProps> = ({
  parentName1,
  parentName2,
  nonCustodialParent,
  calendarData,
  onCalendarChange,
  parentFields,
}) => {
  const { t } = useTranslation();

  // Default active parent to whichever matches the non-custodial parent
  const defaultActive: "parent1" | "parent2" =
    nonCustodialParent === parentName2 ? "parent2" : "parent1";

  const [startDate, setStartDate] = useState<string>(todayISO());
  const [yearRange, setYearRange] = useState<1 | 2>(2);
  const [activeParent, setActiveParent] = useState<"parent1" | "parent2">(defaultActive);
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [assignRestDialogOpen, setAssignRestDialogOpen] = useState(false);
  const [assignRestTarget, setAssignRestTarget] = useState<"parent1" | "parent2">("parent1");

  // Compute date range based on selected year range (1 or 2 years)
  const dateRange = useMemo(() => generateDateRange(startDate, yearRange), [startDate, yearRange]);

  // Determine range boundaries
  const rangeStart = dateRange[0] ?? startDate;
  const rangeEnd = dateRange[dateRange.length - 1] ?? startDate;

  // Compute which years are covered and get holidays
  const holidays = useMemo(() => {
    const years = new Set<number>();
    for (const d of dateRange) {
      years.add(parseInt(d.substring(0, 4), 10));
    }
    return getGeorgiaHolidays(Array.from(years));
  }, [dateRange]);

  // Compute months covered by the range for rendering MonthGrids
  const months = useMemo(() => {
    const result: { year: number; month: number }[] = [];
    const seen = new Set<string>();
    for (const d of dateRange) {
      const year = parseInt(d.substring(0, 4), 10);
      const month = parseInt(d.substring(5, 7), 10) - 1; // 0-indexed
      const key = `${year}-${month}`;
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ year, month });
      }
    }
    return result;
  }, [dateRange]);

  // Count overnights — only count dates within the current range
  const counts = useMemo(() => countOvernights(calendarData, dateRange), [calendarData, dateRange]);
  const total = counts.parent1 + counts.parent2;
  const totalDaysInRange = dateRange.length;
  const remaining = totalDaysInRange - total;

  // Handle date click
  const onDateClick = useCallback(
    (dateStr: string) => {
      const updated = handleDateClick(calendarData, dateStr, activeParent);
      onCalendarChange(updated);
    },
    [calendarData, activeParent, onCalendarChange]
  );

  // Handle pattern population
  const onPopulate = useCallback(
    (pattern: PatternConfig) => {
      const updated = populatePattern(pattern, dateRange, calendarData, activeParent);
      onCalendarChange(updated);
    },
    [dateRange, calendarData, activeParent, onCalendarChange]
  );

  // Assign rest of days to a parent
  const handleAssignRest = () => {
    const updated = { ...calendarData };
    for (const dateStr of dateRange) {
      if (!updated[dateStr]) {
        updated[dateStr] = assignRestTarget;
      }
    }
    onCalendarChange(updated);
    setAssignRestDialogOpen(false);
  };

  // Clear all
  const handleClearConfirm = () => {
    onCalendarChange({});
    setClearDialogOpen(false);
  };

  return (
    <Box sx={{ mt: 2 }} role="region" aria-label={t("calendar.regionLabel")}>
      {/* Georgia CSC Description */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, backgroundColor: "#f8f9fa", borderColor: "#dee2e6" }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
          <InfoOutlinedIcon sx={{ color: "#0464AB", mt: 0.25 }} aria-hidden="true" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0464AB" }}>
            About the Calendar Tool
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          The Georgia Child Support Commission created this tool to help parents, attorneys, and
          child support professionals count the number of days a parent spends with a child in a
          two-year period for an annual average.
        </Typography>
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          For the purposes of this tool, &quot;days&quot; means:
        </Typography>
        <Typography variant="body2" component="div" sx={{ pl: 2, mb: 1.5 }}>
          (A) The total number of overnights a parent spends with the child; or
          <br />
          (B) In circumstances where a parent has shorter but regular and recurring daytime periods
          with a child, the total hours of parenting time in the annual average divided by 24 hours,
          including any hours spent overnight, if applicable.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          The calendar tool calculates an average number of overnights over a two-year period and may
          include a standard schedule, holidays, other overnights and overnight equivalents as
          determined by court order.
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Enter the average number of overnights generated by this tool on Schedule C, Noncustodial
          Parent Parenting Days, of the Georgia Child Support Calculator found at{" "}
          <a
            href="https://csconlinecalc.georgiacourts.gov/"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "#0B57A0" }}
          >
            https://csconlinecalc.georgiacourts.gov/
          </a>
          .
        </Typography>
        <Typography variant="body2">
          If there are multiple children with varying degrees of court ordered parenting time, use
          the average number of court ordered days. In split parenting cases where two worksheets are
          prepared, the parenting time adjustment shall be used when preparing both worksheets.
        </Typography>
      </Paper>

      {/* Instructions & Disclaimers */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, backgroundColor: "#f8f9fa", borderColor: "#dee2e6" }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
          <InfoOutlinedIcon sx={{ color: "#0464AB", mt: 0.25 }} aria-hidden="true" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#0464AB" }}>
            {t("calendar.instructionsTitle")}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("calendar.instructionsBody")}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1 }}>
          {t("calendar.instructionsSteps")}
        </Typography>
        <Alert severity="warning" sx={{ mt: 1 }} role="note">
          <Typography variant="body2">
            {t("calendar.disclaimer")}
          </Typography>
        </Alert>
      </Paper>

      {/* Parent Name Fields */}
      {parentFields}

      {/* Parent Tabs */}
      <ParentTabs
        parentName1={parentName1}
        parentName2={parentName2}
        activeParent={activeParent}
        onTabChange={setActiveParent}
      />

      {/* Controls Row: Start Date + Year Range Toggle */}
      <Box
        sx={{
          mt: 2,
          mb: 2,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          size="small"
          type="date"
          label={t("calendar.startDateLabel")}
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          InputProps={{ sx: { minHeight: 44 } }}
          sx={{ minWidth: 180 }}
          inputProps={{ "aria-label": t("calendar.startDateLabel") }}
        />

        <ToggleButtonGroup
          value={yearRange}
          exclusive
          onChange={(_e, val) => { if (val !== null) setYearRange(val as 1 | 2); }}
          size="small"
          aria-label={t("calendar.yearRangeLabel")}
        >
          <ToggleButton value={1} aria-label={t("calendar.oneYear")} sx={{ minHeight: 44, minWidth: 44 }}>
            {t("calendar.oneYear")}
          </ToggleButton>
          <ToggleButton value={2} aria-label={t("calendar.twoYears")} sx={{ minHeight: 44, minWidth: 44 }}>
            {t("calendar.twoYears")}
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Pattern Controls */}
      <PatternControls onPopulate={onPopulate} />

      {/* Color Legend — WCAG compliant with non-color indicators */}
      <Box
        component="ul"
        role="list"
        aria-label={t("calendar.legendLabel")}
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mt: 2,
          mb: 1,
          alignItems: "center",
          listStyle: "none",
          p: 0,
          m: 0,
        }}
      >
        <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#0277BD",
            }}
            aria-hidden="true"
          />
          <Typography variant="caption">{parentName1}</Typography>
        </Box>
        <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              backgroundColor: "#7B1FA2",
            }}
            aria-hidden="true"
          />
          <Typography variant="caption">{parentName2}</Typography>
        </Box>
        <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              border: "1px solid #e0e0e0",
              backgroundColor: "transparent",
            }}
            aria-hidden="true"
          />
          <Typography variant="caption">{t("calendar.legendUnassigned")}</Typography>
        </Box>
        <Box component="li" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: "#E65100",
              border: "1px solid #fff",
            }}
            aria-hidden="true"
          />
          <Typography variant="caption">{t("calendar.legendHoliday")}</Typography>
        </Box>
      </Box>

      {/* Month Grids */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          mt: 3,
          justifyContent: "center",
        }}
      >
        {months.map(({ year, month }) => (
          <MonthGrid
            key={`${year}-${month}`}
            year={year}
            month={month}
            calendarData={calendarData}
            activeParent={activeParent}
            holidays={holidays}
            onDateClick={onDateClick}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            parentName1={parentName1}
            parentName2={parentName2}
          />
        ))}
      </Box>

      {/* Overnight Counter */}
      <OvernightCounter
        parentName1={parentName1}
        parentName2={parentName2}
        count1={counts.parent1}
        count2={counts.parent2}
        total={total}
        totalDaysInRange={totalDaysInRange}
        yearRange={yearRange}
      />

      {/* Action Buttons — WCAG 2.5.5: minimum 44x44px touch targets */}
      <Box sx={{ mt: 3, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
        {/* Assign Rest of Days */}
        <Button
          variant="contained"
          color="primary"
          disabled={remaining <= 0}
          onClick={() => setAssignRestDialogOpen(true)}
          aria-label={t("calendar.assignRestButton")}
          sx={{ minHeight: 44, minWidth: 44, px: 3 }}
        >
          {t("calendar.assignRestButton")}
        </Button>

        {/* Clear All */}
        <Button
          variant="outlined"
          color="error"
          onClick={() => setClearDialogOpen(true)}
          aria-label={t("calendar.clearAllButton")}
          sx={{ minHeight: 44, minWidth: 44, px: 3 }}
        >
          {t("calendar.clearAllButton")}
        </Button>
      </Box>

      {/* Assign Rest of Days Dialog */}
      <Dialog
        open={assignRestDialogOpen}
        onClose={() => setAssignRestDialogOpen(false)}
        disableRestoreFocus
        aria-labelledby="assign-rest-dialog-title"
      >
        <DialogTitle id="assign-rest-dialog-title">
          {t("calendar.assignRestDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            {t("calendar.assignRestDialogMessage", { count: remaining })}
          </Typography>
          <FormControl size="small" fullWidth>
            <InputLabel id="assign-rest-parent-label">
              {t("calendar.assignRestParentLabel")}
            </InputLabel>
            <Select
              labelId="assign-rest-parent-label"
              value={assignRestTarget}
              label={t("calendar.assignRestParentLabel")}
              onChange={(e) => setAssignRestTarget(e.target.value as "parent1" | "parent2")}
              inputProps={{
                "aria-required": true,
              }}
              sx={{ minHeight: 44 }}
            >
              <MenuItem value="parent1">{parentName1}</MenuItem>
              <MenuItem value="parent2">{parentName2}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAssignRestDialogOpen(false)}
            autoFocus
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            {t("calendar.cancelButton")}
          </Button>
          <Button
            onClick={handleAssignRest}
            variant="contained"
            color="primary"
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            {t("calendar.assignRestConfirmButton")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clear All Confirmation Dialog */}
      <Dialog
        open={clearDialogOpen}
        onClose={() => setClearDialogOpen(false)}
        disableRestoreFocus
        aria-labelledby="clear-all-dialog-title"
      >
        <DialogTitle id="clear-all-dialog-title">
          {t("calendar.clearAllDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography>{t("calendar.clearAllDialogMessage")}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setClearDialogOpen(false)}
            autoFocus
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            {t("calendar.cancelButton")}
          </Button>
          <Button
            onClick={handleClearConfirm}
            color="error"
            variant="contained"
            sx={{ minHeight: 44, minWidth: 44 }}
          >
            {t("calendar.confirmClearButton")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ParentingTimeCalendar;
